/**
 * BOM / 设备 BOM 字段提示（hint）配置
 * --------------------------------------------------------------------------
 * 用于产品 BOM 与设备 BOM 编辑面板中每个字段旁的「问号悬浮提示」。
 * 与 fmeaFieldHints 共用 FieldHintData 结构（required/zh/en），
 * 由通用 <FieldHelpIcon /> 渲染，降低填写门槛。
 *
 * key 命名尽量贴合字段语义；BOM 抽屉属性 Tab 用 BOM_ATTRIBUTE_HINTS，
 * 特性 Tab 用 BOM_CHARACTERISTIC_HINTS；设备 BOM 用 EQUIPMENT_BOM_FIELD_HINTS。
 */
import type { FieldHintData } from '@/components/ui/FieldHelpIcon'

export const BOM_ATTRIBUTE_HINTS: Record<string, FieldHintData> = {
  node_name: {
    required: 'required',
    zh: '节点名称：该零件/总成在产品 BOM 中的名称，例如"驱动电机定子"。建议与图纸、ERP 物料描述保持一致，便于跨部门追溯与协同。',
    en: 'Node name: the part/assembly name in the product BOM, e.g. "Drive Motor Stator". Keep it consistent with drawings and ERP to ease cross-team traceability.',
  },
  node_type: {
    required: 'required',
    zh: '节点类型：assembly=总成（可挂下级零件），part=零件（最底层实物），component=组件（介于两者之间）。类型决定它能否继续展开子节点，搭结构树时先想清层级。',
    en: 'Node type: assembly (can hold children), part (bottom-level physical item), component (in between). Type decides whether it can expand into children — plan the hierarchy first.',
  },
  parent_node: {
    required: 'optional',
    zh: '父节点：该节点在 BOM 层级中的上一级。留空表示顶层（最终产品）。正确设置父子关系才能生成完整的产品结构树与物料需求。',
    en: 'Parent node: the immediate upper level of this node in the BOM hierarchy. Leave blank for the top level (final product). Correct parenting builds the full structure tree and BOM explosion.',
  },
  node_part_number: {
    required: 'optional',
    zh: '零件号：企业内部的物料编码（如 ERP 料号）。用于唯一标识、采购与质量追溯。非必填，但强烈建议填写，否则后续 FMEA/控制计划难以关联。',
    en: 'Part number: the internal material code (e.g. ERP part no.). Used for unique identification, procurement and quality traceability. Optional but strongly recommended — downstream FMEA/control-plan linking depends on it.',
  },
  node_quantity: {
    required: 'required',
    zh: '数量：该父节点下单次装配所用此零件的数量（用量）。必填，直接影响物料需求计算与齐套性。',
    en: 'Quantity: how many of this part are used per assembly of the parent. Required — it drives material requirements and kitting directly.',
  },
  node_material: {
    required: 'optional',
    zh: '材料：该零件的主材，例如"硅钢片""铝合金 ADC12"。用于工艺路线规划、成本估算与可制造性分析。仅实物零件有意义。',
    en: 'Material: the primary material of the part, e.g. "silicon steel", "aluminum ADC12". Used for process routing, cost estimate and manufacturability. Meaningful for physical parts only.',
  },
}

export const BOM_CHARACTERISTIC_HINTS: Record<string, FieldHintData> = {
  char_type: {
    required: 'required',
    zh: '特性类型：dimension=尺寸，physical=物理性能（如硬度/电阻），material=材料成分，visual=外观，functional=功能。类型决定后续检验方式与量具选择。',
    en: 'Characteristic type: dimension, physical (hardness/resistance…), material (composition), visual, functional. Type decides the inspection method and gauging.',
  },
  char_name: {
    required: 'required',
    zh: '特性名称：该质量特性的简称，例如"绕组电阻""表面粗糙度"。应能在图纸或规范中唯一定位。',
    en: 'Characteristic name: a short name for the quality characteristic, e.g. "winding resistance", "surface roughness". It should map uniquely to a drawing/spec callout.',
  },
  char_requirement: {
    required: 'required',
    zh: '要求：特性的技术条件，例如"≤ 0.5 Ω""Ra ≤ 1.6 μm"。尽量带上单位与公差，越具体越便于检验判定。',
    en: 'Requirement: the technical condition, e.g. "≤ 0.5 Ω", "Ra ≤ 1.6 μm". Include unit and tolerance — the more specific, the easier the pass/fail call.',
  },
  char_grade: {
    required: 'optional',
    zh: '等级：CC=关键特性（安全/法规相关），SC=重要特性（性能/耐久相关），MC=一般特性。等级决定管控严格度与特殊特性标识。',
    en: 'Grade: CC = Critical (safety/regulatory), SC = Significant (performance/durability), MC = minor. Grade sets control strictness and special-characteristic marking.',
  },
  char_spec_lower: {
    required: 'optional',
    zh: '规格下限：特性允许的最小值。仅数值型特性填写；与上限共同构成公差带，留空表示单向（只有上限）。',
    en: 'Spec lower limit: the minimum allowed value. For numeric characteristics only; with the upper limit it forms the tolerance band. Leave blank for one-sided specs.',
  },
  char_spec_upper: {
    required: 'optional',
    zh: '规格上限：特性允许的最大值。仅数值型特性填写；与下限共同构成公差带，留空表示单向（只有下限）。',
    en: 'Spec upper limit: the maximum allowed value. For numeric characteristics only; with the lower limit it forms the tolerance band. Leave blank for one-sided specs.',
  },
  char_unit: {
    required: 'optional',
    zh: '单位：特性的计量单位，例如"Ω""mm""μm"。统一单位可避免导出/对接下游工具时歧义。',
    en: 'Unit: the measurement unit, e.g. "Ω", "mm", "μm". Consistent units avoid ambiguity when exporting to downstream tools.',
  },
  char_measurement_method: {
    required: 'optional',
    zh: '测量方法：如何检验该特性，例如"万用表接触测量""三坐标扫描""目视"。写清方法便于编制检验指导书（SIP）。',
    en: 'Measurement method: how to inspect it, e.g. "multimeter contact", "CMM scan", "visual". A clear method helps author the inspection standard (SIP).',
  },
  char_linked_tools: {
    required: 'optional',
    zh: '关联工具：该特性可一键联动推导的下游质量工具，例如"FMEA / 控制计划 / SPC"。打通工具链后，特性数据自动带入，省去重复录入。',
    en: 'Linked tools: downstream quality tools this characteristic can auto-derive into, e.g. "FMEA / Control Plan / SPC". Once linked, data flows through and saves re-entry.',
  },
  char_notes: {
    required: 'optional',
    zh: '备注：补充说明，如抽样方案、历史质量问题、客户特殊要求等。便于团队信息共享。',
    en: 'Notes: extra context such as sampling plan, historical issues, customer-specific requirements. Helps team knowledge sharing.',
  },
}

export const EQUIPMENT_BOM_FIELD_HINTS: Record<string, FieldHintData> = {
  name: {
    required: 'required',
    zh: '名称：设备 BOM 中该设备/部件的名称，例如"前处理电泳槽""循环泵组"。建议与设备台账、资产标签保持一致，便于维保追溯。',
    en: 'Name: the equipment/component name in the Equipment BOM, e.g. "Pretreatment ED Tank", "Circulation Pump Unit". Keep it consistent with the asset register for maintenance traceability.',
  },
  type: {
    required: 'required',
    zh: '类型：system=设备系统（顶层，如整条产线），subsystem=子系统，component=组件，part=零件。类型决定层级深度与 MFMEA 分析粒度。',
    en: 'Type: system (top-level line), subsystem, component, part. Type sets hierarchy depth and the granularity of MFMEA analysis.',
  },
  equipment_code: {
    required: 'optional',
    zh: '设备编码：企业内部的设备资产编号，例如"FUR-001"。用于维修工单、备件管理与 TPM 点检绑定。',
    en: 'Equipment code: the internal asset number, e.g. "FUR-001". Used for work orders, spare-part management and TPM inspection binding.',
  },
  spare_part: {
    required: 'optional',
    zh: '备件：标记该节点是否为关键备件（需常备库存）。勾选后便于维保排程与缺件预警，降低突发停线风险。',
    en: 'Spare part: marks whether this node is a critical spare (keep in stock). Checked, it helps maintenance scheduling and stock-out alerts, cutting unplanned downtime.',
  },
  maintenance_cycle: {
    required: 'optional',
    zh: '维护周期：建议的预防性维护（PM）间隔，例如"1000h""每月"。用于生成保养计划与备件寿命管理。',
    en: 'Maintenance cycle: the suggested preventive-maintenance (PM) interval, e.g. "1000h", "monthly". Drives the PM plan and spare-part life management.',
  },
  sensor_points: {
    required: 'optional',
    zh: '传感器点位：该节点上用于运行监控的测点，逗号分隔，例如"温度传感器 T1, 振动 V2"。这些测点是 MFMEA 探测控制与预测性维护的数据来源。',
    en: 'Sensor points: monitoring points on this node, comma-separated, e.g. "Temp T1, Vibration V2". They feed MFMEA detection controls and predictive maintenance.',
  },
}
